/**
 * Pure edge-routing PLANNER — the global pass that turns a set of board
 * edges + item rects into per-edge SVG path strings and anchors.
 *
 * This is the "decide where every arrow goes" half of the system; the
 * geometric primitives it calls (anchorOnSide / edgePath / railPaths)
 * live in edgeRouting.ts, and the React drawing half (EdgePath) lives in
 * SpaceRenderer.tsx. No React, no DOM here — so the visual harness can call
 * planRoutes() directly and render exactly what the live board renders.
 */

import type { BoardEdge, BoardItem } from "./types.js";
import {
  type Anchor,
  type Rect,
  type Side,
  RAIL_GAP,
  anchorOnSide,
  edgePath,
  pathMidpoint,
  railPaths,
} from "./edgeRouting.js";

/**
 * Pick the side pair (from→to) by maximising "outreach" — the projection of
 * the chord onto each side's outward normal. Key properties:
 *   - Handles negative-outreach cases: when the chosen horizontal side would
 *     bend back on itself (items overlapping along the tangent axis), the
 *     picker naturally selects the vertical axis instead. Only when *all four*
 *     sides would be negative does it settle for the least-negative one.
 *   - Symmetric: reversing the chord direction flips to the complementary
 *     sides, so mutual pairs A→B / B→A share the same walls.
 *   - Small horizontal bias breaks ties in favour of L/R so well-layered
 *     graphs stay horizontal.
 */
const HORIZONTAL_TIEBREAK = 6;

/** Pick the (fromSide, toSide) pair jointly by maximising the worst-case
 *  outward alignment at both ends. "Outward alignment" = projection of the
 *  resulting anchor-to-anchor chord onto each anchor's outward normal — must
 *  be positive for the link to exit/enter without bending back into the node.
 *
 *  Independent per-end picking (the old approach) can produce *crossed*
 *  anchors when nodes overlap on an axis: e.g., A's R-side and B's L-side
 *  both look right from their respective centers, but B.left < A.right so
 *  the resulting chord runs *backward* — a negative-outreach link that
 *  visually cuts through node interiors. Joint scoring sees the actual
 *  anchor geometry and avoids these pairs.
 *
 *  Hysteresis: when a previously-chosen pair scores within HYSTERESIS of the
 *  new best, we keep the old pair. Without it, two near-tied side pairs
 *  swap on every-pixel drag motion ("vibrating arrows"). The margin is a
 *  raw outward-projection delta in space units — small enough that it
 *  doesn't stick to a clearly-wrong pair once the geometry has moved well
 *  past the boundary.
 *
 *  Tiebreak: small horizontal bias keeps well-layered graphs horizontal.
 *  Falls through to the least-negative pair when no positive pair exists.
 */
const SIDE_CHOICE_HYSTERESIS = 24;

function scoreSidePair(
  from: Rect,
  to: Rect,
  fs: Side,
  ts: Side,
): { score: number; worst: number } {
  const fa = anchorOnSide(from, fs, 0.5);
  const ta = anchorOnSide(to, ts, 0.5);
  const fOut = fa.dx * (ta.x - fa.x) + fa.dy * (ta.y - fa.y);
  const tOut = ta.dx * (fa.x - ta.x) + ta.dy * (fa.y - ta.y);
  const worst = Math.min(fOut, tOut);
  const bias =
    (fs === "L" || fs === "R" ? HORIZONTAL_TIEBREAK : 0) +
    (ts === "L" || ts === "R" ? HORIZONTAL_TIEBREAK : 0);
  return { score: worst + bias, worst };
}

/** Liang-Barsky: does segment a→b pass through the OPEN interior of `r`?
 *  Grazing exactly along a wall does not count — chords legally run flush
 *  against neighbouring items. */
function segmentHitsRect(ax: number, ay: number, bx: number, by: number, r: Rect): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  let t0 = 0;
  let t1 = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  if (!clip(-dx, ax - r.x)) return false;
  if (!clip(dx, r.x + r.w - ax)) return false;
  if (!clip(-dy, ay - r.y)) return false;
  if (!clip(dy, r.y + r.h - ay)) return false;
  return t1 - t0 > 1e-9;
}

/** How many obstacles the mid-side chord of a candidate pair stabs through.
 *  The chord approximates the final curve (real anchors get slot fractions
 *  and the path bends), which is enough to steer connections onto walls
 *  with a clear line of sight. */
function chordCrossings(
  from: Rect,
  to: Rect,
  fs: Side,
  ts: Side,
  obstacles: readonly Rect[],
): number {
  const fa = anchorOnSide(from, fs, 0.5);
  const ta = anchorOnSide(to, ts, 0.5);
  let n = 0;
  for (const r of obstacles) {
    if (segmentHitsRect(fa.x, fa.y, ta.x, ta.y, r)) n++;
  }
  return n;
}

export function chooseSides(
  from: Rect,
  to: Rect,
  prev?: { fromSide: Side; toSide: Side },
  obstacles?: readonly Rect[],
): { fromSide: Side; toSide: Side } {
  const sides: Side[] = ["R", "L", "T", "B"];
  type Cand = { score: number; worst: number; fromSide: Side; toSide: Side };
  let best: Cand | null = null;
  const all: Cand[] = [];
  for (const fs of sides) {
    for (const ts of sides) {
      const { score, worst } = scoreSidePair(from, to, fs, ts);
      const cand = { score, worst, fromSide: fs, toSide: ts };
      all.push(cand);
      if (!best || score > best.score) best = cand;
    }
  }

  // Obstacle dodge: when the outreach winner's chord stabs through another
  // item, hand the link to the best-scoring positive-outreach pair with
  // strictly fewer crossings (fewest first). Wrong-facing pairs (negative
  // outreach) never dodge — an arrow bending back around its own node reads
  // worse than one crossing a neighbour. No obstacles → pure outreach, the
  // pre-existing behaviour.
  let chosen = best!;
  let chosenCrossings = 0;
  if (obstacles && obstacles.length > 0) {
    chosenCrossings = chordCrossings(from, to, chosen.fromSide, chosen.toSide, obstacles);
    if (chosenCrossings > 0) {
      let alt: (Cand & { n: number }) | null = null;
      for (const c of all) {
        if (c.worst <= 0) continue;
        const n = chordCrossings(from, to, c.fromSide, c.toSide, obstacles);
        if (n >= chosenCrossings) continue;
        if (!alt || n < alt.n || (n === alt.n && c.score > alt.score)) alt = { ...c, n };
      }
      if (alt) {
        chosen = alt;
        chosenCrossings = alt.n;
      }
    }
  }

  if (prev) {
    const prevScore = scoreSidePair(from, to, prev.fromSide, prev.toSide).score;
    const prevCrossings =
      obstacles && obstacles.length > 0
        ? chordCrossings(from, to, prev.fromSide, prev.toSide, obstacles)
        : 0;
    if (prevCrossings <= chosenCrossings && chosen.score - prevScore <= SIDE_CHOICE_HYSTERESIS) {
      return { fromSide: prev.fromSide, toSide: prev.toSide };
    }
  }
  return { fromSide: chosen.fromSide, toSide: chosen.toSide };
}

/** Plan routes for every edge in a space: side choice, anchor slotting so
 *  multiple edges on the same side fan out, and bidirectional offset. */
export interface PlannedRoute {
  readonly d: string;
  readonly fromAnchor: Anchor;
  readonly toAnchor: Anchor;
  readonly fromSide: Side;
  readonly toSide: Side;
  /** On-path geometric midpoint of `d` (arc length), where edge badges/icons
   *  anchor. NOT the chord midpoint — that floats off curved / axis-aligned
   *  routes. */
  readonly mid: { readonly x: number; readonly y: number };
}

export function planRoutes(
  edges: readonly BoardEdge[],
  items: ReadonlyMap<string, BoardItem>,
  prevSides?: Map<string, { fromSide: Side; toSide: Side }>,
): Map<string, PlannedRoute> {
  const result = new Map<string, PlannedRoute>();
  if (edges.length === 0) return result;

  // Mutual-pair detection up front: pass 1 needs it so both legs of an A↔B
  // pair resolve to ONE side choice, and pass 5 uses it for the rail offsets.
  const pairKey = (a: string, b: string) => `${a}→${b}`;
  const edgeSet = new Set(edges.map((e) => pairKey(e.from, e.to)));

  // Pass 1: pick sides per edge. Pass in the previously-chosen pair so
  // chooseSides can apply hysteresis and avoid flicker during drags.
  //
  // Mutual pairs: only the canonical leg (from ≤ to) runs chooseSides; the
  // reverse leg mirrors it in a second sweep. Scoring is direction-symmetric
  // but its TIE-BREAK (candidate iteration order) is not — equal-size rects on
  // a diagonal make the two mixed side pairs (e.g. R→T and B→L) score
  // identically, and per-leg tie-breaking then routes the two directions down
  // different corridors entirely.
  const sides = new Map<string, { fromSide: Side; toSide: Side }>();
  const sidesByPair = new Map<string, { fromSide: Side; toSide: Side }>();
  const mirrored: BoardEdge[] = [];
  for (const e of edges) {
    const from = items.get(e.from);
    const to = items.get(e.to);
    if (!from || !to) continue;
    if (edgeSet.has(pairKey(e.to, e.from)) && e.from > e.to) {
      mirrored.push(e);
      continue;
    }
    // Obstacles for side choice: sibling items overlapping the from∪to
    // bounding box — the only region a candidate chord can traverse.
    const minX = Math.min(from.x, to.x);
    const minY = Math.min(from.y, to.y);
    const maxX = Math.max(from.x + from.w, to.x + to.w);
    const maxY = Math.max(from.y + from.h, to.y + to.h);
    let obstacles: Rect[] | undefined;
    for (const [id, r] of items) {
      if (id === e.from || id === e.to) continue;
      if (r.x + r.w <= minX || r.x >= maxX || r.y + r.h <= minY || r.y >= maxY) continue;
      (obstacles ??= []).push(r);
    }
    let chosen = chooseSides(from, to, prevSides?.get(e.id), obstacles);
    // Row-anchored edges (e.g. a foreign key into a table column) dock on
    // a horizontal band, so the to-side must be L or R. Pick the wall that
    // faces the source so the link doesn't wrap around the card.
    if (e.toRowFrac != null) {
      const fromCx = from.x + from.w / 2;
      const toCx = to.x + to.w / 2;
      chosen = { fromSide: chosen.fromSide, toSide: fromCx <= toCx ? "L" : "R" };
    }
    sides.set(e.id, chosen);
    sidesByPair.set(pairKey(e.from, e.to), chosen);
  }
  for (const e of mirrored) {
    const fwd = sidesByPair.get(pairKey(e.to, e.from));
    if (!fwd) continue;
    let chosen = { fromSide: fwd.toSide, toSide: fwd.fromSide };
    if (e.toRowFrac != null) {
      const from = items.get(e.from)!;
      const to = items.get(e.to)!;
      const fromCx = from.x + from.w / 2;
      const toCx = to.x + to.w / 2;
      chosen = { fromSide: chosen.fromSide, toSide: fromCx <= toCx ? "L" : "R" };
    }
    sides.set(e.id, chosen);
  }

  // Pass 2: for each (item, side), gather the set of UNIQUE peer items so each
  // peer claims one slot — two-way pairs share one slot per endpoint.
  type SideBuckets = Map<Side, Set<string>>;
  const bucketsByItem = new Map<string, SideBuckets>();
  const touch = (itemId: string, side: Side, peerId: string) => {
    let sb = bucketsByItem.get(itemId);
    if (!sb) {
      sb = new Map();
      bucketsByItem.set(itemId, sb);
    }
    let set = sb.get(side);
    if (!set) {
      set = new Set();
      sb.set(side, set);
    }
    set.add(peerId);
  };
  for (const e of edges) {
    const s = sides.get(e.id);
    if (!s) continue;
    touch(e.from, s.fromSide, e.to);
    touch(e.to, s.toSide, e.from);
  }

  // Pass 3: sort peers along each side by the **angle from the side's
  // midpoint** to the peer. Slope = tangent-component / outward-component,
  // which is monotonic in angle across the range each side cares about.
  // This handles cases where a close peer at a modest angle and a far
  // peer at a flatter angle should both get appropriately-ordered slots
  // without crossing — a pure "sort by peer y" (or x) can't see the angle
  // and sometimes puts them in crossing order.
  const slotFor = new Map<string, Map<Side, Map<string, number>>>();
  const totalSlots = new Map<string, Map<Side, number>>();
  for (const [itemId, sideBuckets] of bucketsByItem) {
    const src = items.get(itemId);
    const perSide = new Map<Side, Map<string, number>>();
    const totals = new Map<Side, number>();
    for (const [side, peerSet] of sideBuckets) {
      const peers = [...peerSet];
      peers.sort((a, b) => {
        const ra = items.get(a);
        const rb = items.get(b);
        if (!ra || !rb || !src) return a.localeCompare(b);
        // Midpoint of the source side — the actual anchor origin.
        let sx: number, sy: number;
        switch (side) {
          case "L": sx = src.x; sy = src.y + src.h / 2; break;
          case "R": sx = src.x + src.w; sy = src.y + src.h / 2; break;
          case "T": sx = src.x + src.w / 2; sy = src.y; break;
          case "B": sx = src.x + src.w / 2; sy = src.y + src.h; break;
        }
        const slope = (rect: { x: number; y: number; w: number; h: number }) => {
          const pcx = rect.x + rect.w / 2;
          const pcy = rect.y + rect.h / 2;
          const dx = pcx - sx;
          const dy = pcy - sy;
          // For L/R sides the tangent is y, outward is ±x. For T/B sides
          // the tangent is x, outward is ±y. Degenerate (outward ≤ 0)
          // pushes the peer to the far end of the sort range so it doesn't
          // collide with well-oriented peers.
          let tan: number, out: number;
          switch (side) {
            case "L": tan = dy; out = -dx; break;
            case "R": tan = dy; out = dx; break;
            case "T": tan = dx; out = -dy; break;
            case "B": tan = dx; out = dy; break;
          }
          if (out <= 0) return tan >= 0 ? Infinity : -Infinity;
          return tan / out;
        };
        return slope(ra) - slope(rb);
      });
      const slots = new Map<string, number>();
      peers.forEach((p, i) => slots.set(p, i));
      perSide.set(side, slots);
      totals.set(side, peers.length);
    }
    slotFor.set(itemId, perSide);
    totalSlots.set(itemId, totals);
  }

  // Pass 4 (mutual-pair detection) moved above pass 1 — side mirroring needs
  // it. Mutual pairs share one slot AND one anchor point per endpoint; the
  // two legs render as constant-gap rails around one center path (see
  // railPaths in edgeRouting).

  // Pass 5: emit routes.
  for (const e of edges) {
    const from = items.get(e.from);
    const to = items.get(e.to);
    const s = sides.get(e.id);
    if (!from || !to || !s) continue;

    const fromSlots = slotFor.get(e.from)!.get(s.fromSide)!;
    const toSlots = slotFor.get(e.to)!.get(s.toSide)!;
    const fromTotal = totalSlots.get(e.from)!.get(s.fromSide)!;
    const toTotal = totalSlots.get(e.to)!.get(s.toSide)!;

    const fFrac = (fromSlots.get(e.to)! + 1) / (fromTotal + 1);
    // Row-anchored target: dock at the column's row fraction instead of the
    // evenly-spaced slot, so the arrowhead lands on the right table row.
    const tFrac =
      e.toRowFrac != null ? e.toRowFrac : (toSlots.get(e.from)! + 1) / (toTotal + 1);

    let fromAnchor = anchorOnSide(from, s.fromSide, fFrac);
    let toAnchor = anchorOnSide(to, s.toSide, tFrac);

    // Hub bundling: when a source side has many edges, lengthen the first
    // control-tangent. Parallel neighbours stay tight against each other near
    // the hub before fanning out — a cheap proxy for edge bundling that
    // reduces visual crossings at busy nodes.
    const fromSideDegree = totalSlots.get(e.from)?.get(s.fromSide) ?? 1;
    const toSideDegree = totalSlots.get(e.to)?.get(s.toSide) ?? 1;
    const hubReach = 1 + 0.15 * (Math.max(fromSideDegree, toSideDegree) - 1);

    // Row-anchored legs opt out of rails: their dock fraction is anchor-
    // specific (a table row), so the reverse leg cannot share a center path.
    const isMutual =
      e.from !== e.to && e.toRowFrac == null && edgeSet.has(pairKey(e.to, e.from));

    // Facing-wall mutual pairs: when the two walls overlap along the shared
    // tangent axis and the pair is alone on both walls, snap both anchors to
    // the overlap midpoint. The center becomes a straight run, so the rails
    // read as true parallel tracks — without this, each leg jogs sideways in
    // mirror image and the tight pair reads as a crossing X. Both legs
    // derive the same midpoint from the rects alone, so they stay in sync.
    if (isMutual && fromTotal === 1 && toTotal === 1) {
      const fs = s.fromSide;
      const ts = s.toSide;
      const horizontalPair = (fs === "L" && ts === "R") || (fs === "R" && ts === "L");
      const verticalPair = (fs === "T" && ts === "B") || (fs === "B" && ts === "T");
      if (horizontalPair || verticalPair) {
        const lo = horizontalPair ? Math.max(from.y, to.y) : Math.max(from.x, to.x);
        const hi = horizontalPair
          ? Math.min(from.y + from.h, to.y + to.h)
          : Math.min(from.x + from.w, to.x + to.w);
        if (hi - lo >= 24) {
          const mid = (lo + hi) / 2;
          const fFracMid = horizontalPair ? (mid - from.y) / from.h : (mid - from.x) / from.w;
          const tFracMid = horizontalPair ? (mid - to.y) / to.h : (mid - to.x) / to.w;
          fromAnchor = anchorOnSide(from, fs, fFracMid);
          toAnchor = anchorOnSide(to, ts, tFracMid);
        }
      }
    }

    let d: string;
    if (isMutual) {
      // ONE center path per pair, always computed in canonical (lower-id →
      // higher-id) orientation so both legs offset the SAME polyline. Each
      // leg takes the rail on its travel-right side and runs it in its own
      // direction — arrowheads land on either side of the shared anchors.
      const canonical = e.from < e.to;
      const cFromAnchor = canonical ? fromAnchor : toAnchor;
      const cToAnchor = canonical ? toAnchor : fromAnchor;
      // Sibling-fan spread for the center, derived from the canonical leg's
      // source side so both legs agree on it.
      const cSlotIdx = (canonical ? fromSlots.get(e.to) : toSlots.get(e.from)) ?? 0;
      const cTotal = canonical ? fromTotal : toTotal;
      const slotSpread = (cSlotIdx - (cTotal - 1) / 2) * 10;
      const center = edgePath(cFromAnchor, cToAnchor, slotSpread, hubReach);
      const rails = railPaths(center, RAIL_GAP);
      d = canonical ? rails.forward : rails.reverse;
    } else {
      // Spread trunks/curves for sibling edges on the same hub side: each
      // gets a per-slot fraction of a fixed spread, so three edges from the
      // same side have three distinct midlines.
      const fromSlotIdx = fromSlots.get(e.to) ?? 0;
      const slotSpread = (fromSlotIdx - (fromTotal - 1) / 2) * 10;
      d = edgePath(fromAnchor, toAnchor, slotSpread, hubReach);
    }

    result.set(e.id, {
      d,
      fromAnchor,
      toAnchor,
      fromSide: s.fromSide,
      toSide: s.toSide,
      mid: pathMidpoint(d),
    });
  }

  return result;
}
